import { Alert } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncService } from '../../../../expressions/repository/async/useAsyncService.ts';
import { useAsyncServiceGetData } from '../../../../expressions/repository/async/useAsyncServiceGetData.ts';
import { useWidgetScopeContext } from '../../../../form/fillable/WidgetScopeContext.tsx';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { getWidgetProps } from '../common/util/getWidgetProps.ts';
import { AsyncSelectWidgetSelectInput } from './components/select/AsyncSelectWidgetSelectInput.tsx';
import { AsyncServiceTableInput } from './components/table/AsyncServiceTableInput.tsx';
import { asyncSelectWidgetObjectName } from './hooks/asyncSelectObjectName.ts';
import { type IAsyncSelectWidget } from './IAsyncSelectWidget.ts';

export type IAsyncSelectWidgetComponentProps =
  IWidgetComponentProps<IAsyncSelectWidget>;

export function AsyncSelectWidgetComponent(
  props: IAsyncSelectWidgetComponentProps,
) {
  const { widget } = props;
  const widgetOptions = getWidgetProps(props);

  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const { prefix } = useWidgetScopeContext();
  const { t } = useTranslation();

  const { service, axiosClient } = useAsyncService(widget.serviceId);

  // Se configurato il "filtro di ricerca", viene usato direttamente
  // come parametro di ricerca del servizio
  const searchParam = useMemo(() => {
    return widget.searchField && searchQuery
      ? { [widget.searchField]: searchQuery }
      : undefined;
  }, [widget.searchField, searchQuery]);

  const objectFieldName = useMemo(
    () => `${prefix}${asyncSelectWidgetObjectName(widget.code)}`,
    [prefix, widget.code],
  );

  const { data, error, loading } = useAsyncServiceGetData({
    basePath: prefix,
    client: axiosClient,
    service,
    inputs: widget.inputs,
    searchParam,
    disabled: widgetOptions.readonly,
  });

  const hasInvalidData = data != null && !Array.isArray(data);
  const asyncSelectData = hasInvalidData ? null : data;

  if (hasInvalidData) {
    return (
      <Alert
        type="error"
        message={
          service?.name
            ? t(
                'widgets.async-select-widget-component.invalid-data-format',
                'Il servizio "{{service}}" ha restituito dati in un formato non valido.',
                { service: service.name },
              )
            : t(
                'widgets.async-select-widget-component.invalid-data-format-no-service',
                'Il servizio ha restituito dati in un formato non valido.',
              )
        }
      />
    );
  }

  if (widget.renderType === 'table') {
    return (
      <AsyncServiceTableInput
        widgetOptions={widgetOptions}
        data={asyncSelectData}
        isLoading={loading}
        error={error}
        widget={widget}
        setSearchQuery={setSearchQuery}
        objectFieldName={objectFieldName}
        service={service}
      />
    );
  }

  return (
    <AsyncSelectWidgetSelectInput
      widget={widget}
      widgetOptions={widgetOptions}
      readonly={props.readonly}
      data={asyncSelectData}
      isLoading={loading}
      error={error}
      objectFieldName={objectFieldName}
      service={service}
      setSearchQuery={setSearchQuery}
      searchQuery={searchQuery}
      name={props.name}
    />
  );
}
